import { get, set, del } from 'idb-keyval';

/**
 * v5 — Personnalisation du bouton "Cliquez pour commander" de l'écran de veille
 *
 * Différent du bouton Commander du panier :
 * - Sur fond sombre (écran de veille)
 * - TOUJOURS animé (pas seulement au hover)
 * - Plus gros et plus visible (attire le client à toucher)
 * - Supporte une Lottie personnalisée à l'intérieur
 */

export type StandbyTemplate =
  | 'cosmic'       // 1 - Le design actuel (violet/rose halo)
  | 'neonCyan'     // 2 - Néon cyan flashy
  | 'rainbow'      // 3 - Arc-en-ciel animé
  | 'redPulse'     // 4 - Rouge avec onde
  | 'rainbowBorder'// 5 - Bordure multi-couleur
  | 'gold3d'       // 6 - 3D doré
  | 'glass'        // 7 - Glassmorphism
  | 'blackGoldElec'// 8 - Black gold electric
  | 'custom';

export type BgMode = 'solid' | 'gradient' | 'transparent' | 'glass';
export type IconType =
  | 'cart'
  | 'arrow'
  | 'check'
  | 'heart'
  | 'flame'
  | 'star'
  | 'sparkle'
  | 'bolt'
  | 'dot'
  | 'none';
export type IconPosition = 'left' | 'right';
export type Radius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'pill';
export type BorderStyle = 'none' | 'thin' | 'medium' | 'thick' | 'rainbow';
export type GlowType = 'none' | 'soft' | 'neon' | 'mega' | 'orbs';
export type GlowIntensity = 'low' | 'medium' | 'high' | 'extreme';
export type AnimType =
  | 'none'
  | 'cosmicGlow'
  | 'neonFlash'
  | 'rainbow'
  | 'pulseRing'
  | 'rainbowBorder'
  | 'shimmer'
  | 'electricShake'
  | 'float'
  | 'bounce';
export type AnimSpeed = 'slow' | 'medium' | 'fast';
export type BtnSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type VerticalPos = 'top' | 'middle' | 'bottom';

export interface StandbyButtonConfig {
  enabled: boolean;
  template: StandbyTemplate;
  // === Texte ===
  customText: string;          // "" = t('Cliquez pour commander')
  textGradient: boolean;       // texte multi-couleur
  textGradColor1: string;
  textGradColor2: string;
  textGradColor3: string;
  // === Icône ===
  icon: IconType;
  iconPosition: IconPosition;
  // === Lottie custom (NOUVEAU) ===
  showLottie: boolean;         // afficher une animation Lottie en plus
  lottieSize: 'sm' | 'md' | 'lg';
  lottiePosition: 'left' | 'right';
  hasCustomLottie: boolean;    // si l'admin a uploadé un Lottie
  // === Couleurs ===
  bgMode: BgMode;
  bgColor1: string;
  bgColor2: string;
  gradientAngle: number;
  textColor: string;
  // === Forme ===
  radius: Radius;
  borderStyle: BorderStyle;
  borderColor: string;
  // === Effet halo ===
  glow: GlowType;
  glowColor: string;
  glowIntensity: GlowIntensity;
  // === Animation (TOUJOURS active) ===
  animation: AnimType;
  animSpeed: AnimSpeed;
  // === Taille & position ===
  size: BtnSize;
  verticalPos: VerticalPos;
}

const STORAGE_KEY = 'standbyButtonDesign';
const CUSTOM_LOTTIE_KEY = 'standbyButtonLottie';
export const STANDBY_LOTTIE_MAX_SIZE = 2 * 1024 * 1024; // 2 MB

export const DEFAULT_STANDBY_BUTTON: StandbyButtonConfig = {
  enabled: true,
  template: 'cosmic',
  customText: '',
  textGradient: true,
  textGradColor1: '#93c5fd',
  textGradColor2: '#c4b5fd',
  textGradColor3: '#f9a8d4',
  icon: 'dot',
  iconPosition: 'left',
  showLottie: false,
  lottieSize: 'md',
  lottiePosition: 'left',
  hasCustomLottie: false,
  bgMode: 'solid',
  bgColor1: '#0a0a0a',
  bgColor2: '#1a1a1a',
  gradientAngle: 135,
  textColor: '#ffffff',
  radius: 'lg',
  borderStyle: 'thin',
  borderColor: 'rgba(255,255,255,0.2)',
  glow: 'mega',
  glowColor: '#8b5cf6',
  glowIntensity: 'high',
  animation: 'cosmicGlow',
  animSpeed: 'medium',
  size: 'xl',
  verticalPos: 'bottom',
};

/* ============================================
 * 8 Templates pré-faits
 * ============================================ */
export const STANDBY_TEMPLATES: Record<
  Exclude<StandbyTemplate, 'custom'>,
  { label: string; emoji: string; description: string; config: Partial<StandbyButtonConfig> }
> = {
  cosmic: {
    label: 'Cosmique',
    emoji: '🦄',
    description: 'Halo violet/rose, texte dégradé',
    config: {
      template: 'cosmic',
      bgMode: 'solid',
      bgColor1: '#0a0a0a',
      bgColor2: '#1a1a1a',
      textColor: '#ffffff',
      textGradient: true,
      textGradColor1: '#93c5fd',
      textGradColor2: '#c4b5fd',
      textGradColor3: '#f9a8d4',
      radius: 'lg',
      borderStyle: 'thin',
      borderColor: 'rgba(255,255,255,0.2)',
      glow: 'mega',
      glowColor: '#8b5cf6',
      glowIntensity: 'high',
      animation: 'cosmicGlow',
      animSpeed: 'medium',
      icon: 'dot',
    },
  },
  neonCyan: {
    label: 'Néon Cyan',
    emoji: '💎',
    description: 'Cyan flashy futuriste',
    config: {
      template: 'neonCyan',
      bgMode: 'transparent',
      bgColor1: 'transparent',
      bgColor2: 'transparent',
      textColor: '#00ffff',
      textGradient: false,
      radius: 'md',
      borderStyle: 'medium',
      borderColor: '#00ffff',
      glow: 'neon',
      glowColor: '#00ffff',
      glowIntensity: 'high',
      animation: 'neonFlash',
      animSpeed: 'medium',
      icon: 'bolt',
    },
  },
  rainbow: {
    label: 'Arc-en-ciel',
    emoji: '🌈',
    description: 'Dégradé multi-couleur',
    config: {
      template: 'rainbow',
      bgMode: 'gradient',
      bgColor1: '#ff006e',
      bgColor2: '#8338ec',
      gradientAngle: 45,
      textColor: '#ffffff',
      textGradient: false,
      radius: 'lg',
      borderStyle: 'none',
      glow: 'soft',
      glowColor: '#ec4899',
      glowIntensity: 'medium',
      animation: 'rainbow',
      animSpeed: 'medium',
      icon: 'cart',
    },
  },
  redPulse: {
    label: 'Rouge Pulse',
    emoji: '❤️',
    description: 'Onde pulsante rouge',
    config: {
      template: 'redPulse',
      bgMode: 'solid',
      bgColor1: '#ef4444',
      bgColor2: '#ef4444',
      textColor: '#ffffff',
      textGradient: false,
      radius: 'pill',
      borderStyle: 'none',
      glow: 'soft',
      glowColor: '#ef4444',
      glowIntensity: 'medium',
      animation: 'pulseRing',
      animSpeed: 'medium',
      icon: 'flame',
    },
  },
  rainbowBorder: {
    label: 'Bordure Rainbow',
    emoji: '🎨',
    description: 'Contour qui change',
    config: {
      template: 'rainbowBorder',
      bgMode: 'solid',
      bgColor1: 'rgba(15,23,42,0.95)',
      bgColor2: 'rgba(15,23,42,0.95)',
      textColor: '#ffffff',
      textGradient: false,
      radius: 'lg',
      borderStyle: 'rainbow',
      borderColor: '#ec4899',
      glow: 'soft',
      glowColor: '#8b5cf6',
      glowIntensity: 'medium',
      animation: 'rainbowBorder',
      animSpeed: 'medium',
      icon: 'arrow',
    },
  },
  gold3d: {
    label: '3D Or',
    emoji: '💰',
    description: 'Bouton 3D doré pressable',
    config: {
      template: 'gold3d',
      bgMode: 'gradient',
      bgColor1: '#fbbf24',
      bgColor2: '#f59e0b',
      gradientAngle: 135,
      textColor: '#1f2937',
      textGradient: false,
      radius: 'md',
      borderStyle: 'none',
      glow: 'soft',
      glowColor: '#b45309',
      glowIntensity: 'high',
      animation: 'bounce',
      animSpeed: 'medium',
      icon: 'cart',
    },
  },
  glass: {
    label: 'Glassmorphism',
    emoji: '✨',
    description: 'Verre dépoli moderne',
    config: {
      template: 'glass',
      bgMode: 'glass',
      bgColor1: 'rgba(255,255,255,0.05)',
      bgColor2: 'rgba(255,255,255,0.05)',
      textColor: '#ffffff',
      textGradient: false,
      radius: 'lg',
      borderStyle: 'thin',
      borderColor: 'rgba(255,255,255,0.2)',
      glow: 'soft',
      glowColor: 'rgba(255,255,255,0.3)',
      glowIntensity: 'medium',
      animation: 'float',
      animSpeed: 'slow',
      icon: 'sparkle',
    },
  },
  blackGoldElec: {
    label: 'Black Gold Electric',
    emoji: '⚡',
    description: 'Noir & or vibrant',
    config: {
      template: 'blackGoldElec',
      bgMode: 'solid',
      bgColor1: '#1f2937',
      bgColor2: '#1f2937',
      textColor: '#fbbf24',
      textGradient: false,
      radius: 'sm',
      borderStyle: 'thin',
      borderColor: '#fbbf24',
      glow: 'neon',
      glowColor: '#fbbf24',
      glowIntensity: 'medium',
      animation: 'electricShake',
      animSpeed: 'fast',
      icon: 'bolt',
    },
  },
};

export function applyStandbyTemplate(
  current: StandbyButtonConfig,
  tplId: Exclude<StandbyTemplate, 'custom'>
): StandbyButtonConfig {
  return {
    ...current,
    ...STANDBY_TEMPLATES[tplId].config,
    template: tplId,
  };
}

/* ============================================
 * Storage
 * ============================================ */
export async function getStandbyButtonDesign(): Promise<StandbyButtonConfig> {
  try {
    const stored = await get<StandbyButtonConfig>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      return { ...DEFAULT_STANDBY_BUTTON, ...stored };
    }
  } catch (e) {
    console.error('[StandbyButton] load error', e);
  }
  return DEFAULT_STANDBY_BUTTON;
}

export async function saveStandbyButtonDesign(
  cfg: StandbyButtonConfig
): Promise<boolean> {
  try {
    await set(STORAGE_KEY, cfg);
    window.dispatchEvent(
      new CustomEvent('standbyButtonDesignUpdated', { detail: cfg })
    );
    return true;
  } catch (e) {
    console.error('[StandbyButton] save error', e);
    return false;
  }
}

/* ============================================
 * Lottie custom
 * ============================================ */
export async function getCustomStandbyLottie(): Promise<any | null> {
  try {
    return (await get(CUSTOM_LOTTIE_KEY)) || null;
  } catch {
    return null;
  }
}

export async function saveCustomStandbyLottie(json: any): Promise<boolean> {
  try {
    await set(CUSTOM_LOTTIE_KEY, json);
    window.dispatchEvent(new CustomEvent('standbyCustomLottieUpdated'));
    return true;
  } catch {
    return false;
  }
}

export async function deleteCustomStandbyLottie(): Promise<boolean> {
  try {
    await del(CUSTOM_LOTTIE_KEY);
    window.dispatchEvent(new CustomEvent('standbyCustomLottieUpdated'));
    return true;
  } catch {
    return false;
  }
}

export function isValidLottieJson(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  return (
    typeof obj.v === 'string' &&
    typeof obj.fr === 'number' &&
    typeof obj.ip === 'number' &&
    typeof obj.op === 'number' &&
    typeof obj.w === 'number' &&
    typeof obj.h === 'number' &&
    Array.isArray(obj.layers)
  );
}

/* ============================================
 * Helpers de style
 * ============================================ */

const RADIUS_PX: Record<Radius, string> = {
  none: '0',
  sm: '6px',
  md: '10px',
  lg: '14px',
  xl: '20px',
  pill: '9999px',
};

const BORDER_PX: Record<BorderStyle, string> = {
  none: '0',
  thin: '1px',
  medium: '2px',
  thick: '3px',
  rainbow: '2px',
};

const SIZE_STYLES: Record<BtnSize, { padding: string; fontSize: string; scale: number }> = {
  sm: { padding: '10px 16px', fontSize: '14px', scale: 1 },
  md: { padding: '14px 24px', fontSize: '16px', scale: 1.2 },
  lg: { padding: '18px 32px', fontSize: '18px', scale: 1.5 },
  xl: { padding: '24px 48px', fontSize: '22px', scale: 2 },
  xxl: { padding: '30px 60px', fontSize: '28px', scale: 2.5 },
};

const INTENSITY_MULT: Record<GlowIntensity, number> = {
  low: 0.5,
  medium: 1,
  high: 1.5,
  extreme: 2.2,
};

const ANIM_DURATION: Record<AnimSpeed, string> = {
  slow: '2.8s',
  medium: '1.8s',
  fast: '1s',
};

const ANIM_CLASS: Record<AnimType, string> = {
  none: '',
  cosmicGlow: 'standby-btn-cosmic',
  neonFlash: 'standby-btn-neon',
  rainbow: 'standby-btn-rainbow',
  pulseRing: 'standby-btn-pulse',
  rainbowBorder: 'standby-btn-rainbow-border',
  shimmer: 'standby-btn-shimmer',
  electricShake: 'standby-btn-electric',
  float: 'standby-btn-float',
  bounce: 'standby-btn-bounce',
};

const VPOS_STYLE: Record<VerticalPos, string> = {
  top: '20%',
  middle: '50%',
  bottom: '80%',
};

export function getStandbyButtonStyles(cfg: StandbyButtonConfig): {
  containerStyle: React.CSSProperties;
  buttonStyle: React.CSSProperties;
  className: string;
  textStyle: React.CSSProperties;
} {
  const size = SIZE_STYLES[cfg.size];
  const radius = RADIUS_PX[cfg.radius];
  const borderW = BORDER_PX[cfg.borderStyle];
  const intensity = INTENSITY_MULT[cfg.glowIntensity];
  const animDur = ANIM_DURATION[cfg.animSpeed];
  const animCls = ANIM_CLASS[cfg.animation];

  // Background
  let background: string;
  if (cfg.bgMode === 'gradient') {
    background = `linear-gradient(${cfg.gradientAngle}deg, ${cfg.bgColor1}, ${cfg.bgColor2})`;
  } else if (cfg.bgMode === 'transparent') {
    background = 'transparent';
  } else if (cfg.bgMode === 'glass') {
    background = cfg.bgColor1;
  } else {
    background = cfg.bgColor1;
  }

  // Glow / shadow
  const glowCol = cfg.glowColor || cfg.bgColor1;
  let boxShadow = 'none';
  switch (cfg.glow) {
    case 'soft':
      boxShadow = `0 ${4 * intensity}px ${20 * intensity}px ${glowCol}99`;
      break;
    case 'neon':
      boxShadow = `0 0 ${10 * intensity}px ${glowCol}, 0 0 ${30 * intensity}px ${glowCol}, 0 0 ${60 * intensity}px ${glowCol}88`;
      break;
    case 'mega':
      boxShadow = `0 0 ${30 * intensity}px ${glowCol}99, 0 0 ${60 * intensity}px ${glowCol}66, 0 0 ${90 * intensity}px ${glowCol}33`;
      break;
    case 'orbs':
      boxShadow = `0 0 ${40 * intensity}px ${glowCol}66, 0 ${10 * intensity}px ${50 * intensity}px rgba(0,0,0,0.5)`;
      break;
    case 'none':
    default:
      boxShadow = 'none';
  }

  // Conteneur (positionnement)
  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    top: VPOS_STYLE[cfg.verticalPos],
    transform: `translate(-50%, -50%) scale(${size.scale})`,
    zIndex: 60,
  };

  // Style du bouton
  const buttonStyle: React.CSSProperties = {
    background,
    color: cfg.textColor,
    padding: size.padding,
    fontSize: size.fontSize,
    borderRadius: radius,
    border: cfg.borderStyle === 'none' ? 'none' : `${borderW} solid ${cfg.borderColor}`,
    boxShadow,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'transform 0.2s ease, filter 0.2s ease',
    backdropFilter: cfg.bgMode === 'glass' ? 'blur(20px)' : 'none',
    WebkitBackdropFilter: cfg.bgMode === 'glass' ? 'blur(20px)' : 'none',
    position: 'relative',
    overflow: 'visible',
    // CSS vars pour les animations
    ['--standby-glow' as any]: glowCol,
    ['--standby-anim-dur' as any]: animDur,
    ['--standby-intensity' as any]: String(intensity),
  };

  // Style du texte (avec gradient si activé)
  let textStyle: React.CSSProperties = {
    color: cfg.textColor,
    position: 'relative',
    zIndex: 1,
  };
  if (cfg.textGradient) {
    textStyle = {
      ...textStyle,
      background: `linear-gradient(90deg, ${cfg.textGradColor1}, ${cfg.textGradColor2}, ${cfg.textGradColor3})`,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      color: 'transparent',
    };
  }

  return {
    containerStyle,
    buttonStyle,
    className: `standby-btn-custom ${animCls}`.trim(),
    textStyle,
  };
}
